import { Fragment, useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, Play, Clock, RotateCw, Zap, X } from "lucide-react";
import { useOrganizations } from "../../hooks/useOrganizations";
import useExecutionsList from "../../hooks/useExecutionsList";
import "./RunsView.css";

const STATUS_CONFIG = {
  running:   { label: "Running",   bg: "bg-blue-500/15",    text: "text-blue-300",    dot: "bg-blue-400" },
  waiting:   { label: "Waiting",   bg: "bg-amber-500/15",   text: "text-amber-300",   dot: "bg-amber-400" },
  paused:    { label: "Waiting",   bg: "bg-amber-500/15",   text: "text-amber-300",   dot: "bg-amber-400" },
  completed: { label: "Completed", bg: "bg-emerald-500/15", text: "text-emerald-300", dot: "bg-emerald-400" },
  failed:    { label: "Failed",    bg: "bg-rose-500/15",    text: "text-rose-300",    dot: "bg-rose-400" },
  cancelled: { label: "Cancelled", bg: "bg-zinc-700/40",    text: "text-zinc-400",    dot: "bg-zinc-500" },
  expired:   { label: "Expired",   bg: "bg-zinc-800",       text: "text-zinc-500",    dot: "bg-zinc-600" },
  pending:   { label: "Pending",   bg: "bg-zinc-500/15",    text: "text-zinc-300",    dot: "bg-zinc-400" },
};

// Este mapa hace dos trabajos: pone nombre y color a la insignia de cada fila,
// y **es de donde sale el desplegable de Trigger**. Lo segundo es lo que lo
// vuelve peligroso: un disparador que no esté aquí no se puede filtrar desde la
// UI aunque el backend sepa filtrarlo. Pasó con `instagram` y
// `executable_by_workflow`, que son de los más usados y no estaban.
//
// `label` corto a propósito: la columna mide 80px. "executable_by_workflow" en
// crudo se salía y se montaba encima del Execution ID.
const TRIGGER_CONFIG = {
  manual:    { label: "Manual" },
  scheduled: { label: "Schedule" },
  webhook:   { label: "Webhook" },
  api_call:  { label: "API" },
  api:       { label: "API" },
  shopify:   { label: "Shopify" },
  whatsapp:  { label: "WhatsApp" },
  instagram: { label: "Instagram" },
  // "Workflow" y no "By workflow": la etiqueta larga mide 88px y se truncaba a
  // "By workfl…" dentro de la columna de 80. Debajo de la cabecera TRIGGER,
  // "Workflow" ya se lee como "lo disparó otro workflow". Medido: 68px.
  executable_by_workflow: { label: "Workflow" },
};

const TRIGGER_COLORS = {
  manual:    "bg-zinc-500/15 text-zinc-300",
  scheduled: "bg-blue-500/15 text-blue-300",
  webhook:   "bg-orange-500/15 text-orange-300",
  api_call:  "bg-cyan-500/15 text-cyan-300",
  api:       "bg-cyan-500/15 text-cyan-300",
  shopify:   "bg-purple-500/15 text-purple-300",
  whatsapp:  "bg-emerald-500/15 text-emerald-300",
  instagram: "bg-pink-500/15 text-pink-300",
  executable_by_workflow: "bg-indigo-500/15 text-indigo-300",
};

// De `executable_by_workflow` a "Executable by workflow", para los que aparezcan
// en los datos y no estén arriba. No es tan bueno como una etiqueta escrita,
// pero es infinitamente mejor que no poder filtrar por ellos.
const humanizeTrigger = (type) =>
  type.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const PERIODS = [
  { id: "1h",  label: "Last hour",    hours: 1 },
  { id: "24h", label: "Last 24 h",    hours: 24 },
  { id: "7d",  label: "Last 7 days",  hours: 24 * 7 },
  { id: "30d", label: "Last 30 days", hours: 24 * 30 },
  { id: "all", label: "All time",     hours: null },
];

const LIMIT = 50;

// El backend rechaza con 400 una búsqueda de menos de 3 caracteres: sobre
// subcadena, un término tan corto recorre medio proyecto y no le sirve a nadie.
// Aquí se respeta el mismo tope para no pedir lo que ya sabemos que va a
// fallar — el usuario ve un aviso mientras teclea, no un error.
const MIN_SEARCH = 3;

// Lo que se espera entre teclas antes de preguntar. La lista tarda segundos:
// sin esto, escribir "sunchild" son ocho lecturas y la que gana es la que
// conteste última, no la del término completo.
const SEARCH_DEBOUNCE_MS = 350;

const StatusBadge = ({ status }) => {
  const key = status?.toLowerCase();
  const s = STATUS_CONFIG[key] || STATUS_CONFIG.pending;
  const isRunning = key === "running";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ${s.bg} ${s.text} px-2 py-0.5 text-[11px] font-medium`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot} ${isRunning ? "animate-pulse" : ""}`} />
      {s.label}
    </span>
  );
};

const TriggerBadge = ({ type }) => {
  const key = type?.toLowerCase() || "";
  const cfg = TRIGGER_CONFIG[key];
  const colorClass = TRIGGER_COLORS[key] || "bg-zinc-500/15 text-zinc-300";
  const label = cfg?.label || type || "Unknown";
  return (
    <span
      title={label}
      className={`inline-flex max-w-full items-center gap-1 truncate rounded-full ${colorClass} px-2 py-0.5 text-[11px] font-medium`}
    >
      {label}
    </span>
  );
};

const formatDuration = (startedAt, completedAt) => {
  if (!startedAt || !completedAt) return "—";
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(0)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
};

const formatStarted = (startedAt) => {
  if (!startedAt) return "—";
  const diff = Date.now() - new Date(startedAt).getTime();
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 172800000) return "yesterday";
  return `${Math.floor(diff / 86400000)}d ago`;
};

const shortenId = (id) => {
  if (!id) return "—";
  const s = id.toString();
  if (s.length <= 12) return s;
  return `${s.substring(0, 4)}...${s.substring(s.length - 4)}`;
};

const VersionBadge = ({ isTest, version }) => {
  if (isTest) {
    return (
      <span className="inline-flex items-center rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-medium text-amber-300">
        Draft
      </span>
    );
  }
  if (!version) return <span className="text-xs text-zinc-600">—</span>;
  return (
    <span className="inline-flex items-center rounded-md bg-zinc-800/60 px-1.5 py-0.5 text-[11px] font-medium text-zinc-300">
      v{version}
    </span>
  );
};

const coinsFormatter = new Intl.NumberFormat("en-US");

// `null` no es `0`. `null` significa que la corrida no registró intentos —pasa
// con todo lo anterior al 2026-08-20, cuando el backend empezó a persistirlos—
// y se pinta `—`. `0` significa que costó cero de verdad y se pinta `0`. Un
// `cost?.coins || "—"` colapsa los dos casos y le diría a alguien que su
// corrida fue gratis cuando lo que pasa es que no se midió.
const formatCoins = (cost) => {
  const coins = cost?.coins;
  if (typeof coins !== "number" || Number.isNaN(coins)) return "—";
  return coinsFormatter.format(coins);
};

// Una corrida admite hasta 50 etiquetas, así que pintarlas todas rompería la
// fila. Se enseñan las primeras y el resto se cuenta: el detalle de la corrida
// es donde están todas. `title` lleva la lista entera para quien pase por
// encima sin tener que abrir nada.
const TagList = ({ tags, max = 2, className = "", onSelectTag }) => {
  if (!Array.isArray(tags) || tags.length === 0) {
    return <span className="text-xs text-zinc-600">—</span>;
  }
  const shown = tags.slice(0, max);
  const rest = tags.length - shown.length;
  return (
    <span className={`flex min-w-0 items-center gap-1 overflow-hidden ${className}`} title={tags.join(", ")}>
      {/* Sin `shrink-0` en las etiquetas: cuando la columna se queda corta se
          encogen y se recortan cada una con sus puntos suspensivos. Con él, la
          primera se llevaba el ancho entero y la segunda quedaba cortada por la
          mitad contra el borde. El `+N` sí es `shrink-0`: es la señal de que
          hay más, y es lo último que debe desaparecer. */}
      {shown.map((tag) =>
        onSelectTag ? (
          // Pulsar una etiqueta la escribe en la caja en vez de aplicar un
          // filtro invisible: así se ve por qué está filtrada la lista y se
          // puede corregir el término sin adivinar de dónde salió.
          //
          // `stopPropagation` porque la fila entera abre la corrida.
          <button
            key={tag}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelectTag(tag);
            }}
            title={`Buscar "${tag}"`}
            className="min-w-0 max-w-[96px] truncate rounded bg-zinc-800/80 px-1.5 py-0.5 text-[10px] text-zinc-300 transition hover:bg-zinc-700 hover:text-white"
          >
            {tag}
          </button>
        ) : (
          <span
            key={tag}
            className="min-w-0 max-w-[96px] truncate rounded bg-zinc-800/80 px-1.5 py-0.5 text-[10px] text-zinc-300"
          >
            {tag}
          </span>
        )
      )}
      {rest > 0 && <span className="shrink-0 text-[10px] text-zinc-500">+{rest}</span>}
    </span>
  );
};

// `agentId` acota la tabla a las corridas de un agente. Es lo que permite que
// "como le ha ido a este agente" sea una pestaña suya en vez de obligar a ir a
// otra seccion y filtrar a mano.
export default function RunsView({ projectId, agentId, onSelectRun }) {
  const { currentAccount } = useOrganizations();
  const [statusFilter, setStatusFilter] = useState("ALL");
  // Dos estados y no uno: `search` es lo que se está tecleando y `appliedSearch`
  // lo que ya viajó al servidor. Separarlos es lo que permite escribir sin que
  // cada tecla dispare una lectura, y lo que deja enseñar "escribe 3 o más"
  // sin haber pedido nada.
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [triggerFilter, setTriggerFilter] = useState("ALL");
  const [period, setPeriod] = useState("7d");
  const [page, setPage] = useState(0);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);

  // Menos de `MIN_SEARCH` no se manda: cuenta como "sin búsqueda", así que
  // borrar hasta dejar una letra devuelve la lista entera en vez de dejarla
  // congelada en el último resultado bueno.
  useEffect(() => {
    const term = search.trim();
    const next = term.length >= MIN_SEARCH ? term : "";
    if (next === appliedSearch) return undefined;
    const t = setTimeout(() => {
      setAppliedSearch(next);
      // Un término nuevo describe otro conjunto: seguir en la página 4 de la
      // búsqueda anterior enseñaría una página vacía de algo que sí tiene
      // resultados.
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search, appliedSearch]);

  // Red de seguridad para el mapa de arriba: cualquier disparador que llegue en
  // los datos y no esté escrito a mano se ofrece igual en el desplegable. Se
  // acumula y nunca se olvida, porque al filtrar por uno el resultado sólo trae
  // ése y una lista derivada de la página actual se quedaría con una sola
  // opción — justo la que ya está aplicada.
  const [extraTriggers, setExtraTriggers] = useState([]);

  const params = useMemo(() => {
    const built = {
      account_id: currentAccount?.id,
      project_id: projectId,
      limit: LIMIT,
      offset: page * LIMIT,
      sort_by: "started_at",
      sort_order: "desc",
    };
    if (agentId) built.agent_id = agentId;
    if (appliedSearch) built.search = appliedSearch;
    if (statusFilter !== "ALL") {
      // "Waiting" no es un status del backend; agrupa los estados de espera reales.
      // El resto de los tabs coinciden 1:1 con el enum (running/failed/completed).
      built.status_filter =
        statusFilter === "WAITING"
          ? ["awaiting_input", "paused", "waiting_for_webhook"]
          : [statusFilter.toLowerCase()];
    }
    if (triggerFilter !== "ALL") {
      built.trigger_type_filter = [triggerFilter.toLowerCase()];
    }
    const selectedPeriod = PERIODS.find(p => p.id === period);
    if (selectedPeriod?.hours) {
      // La ventana se redondea a la hora para que la clave de caché no cambie
      // en cada render — con `Date.now()` exacto, volver a la tabla siempre
      // era una lectura nueva.
      const hourStart = Math.floor(Date.now() / 3600000) * 3600000;
      built.start_date = new Date(hourStart - selectedPeriod.hours * 3600 * 1000).toISOString();
    }
    return built;
  }, [currentAccount?.id, projectId, agentId, statusFilter, triggerFilter, period, page, appliedSearch]);

  // Un refresco explícito no vacía la tabla: la atenúa. Reemplazarla por el
  // spinner esconde justamente lo que el usuario está mirando para comparar.
  const { executions, totalCount, loading, refreshing, error, reload } = useExecutionsList(
    params,
    { enabled: Boolean(projectId && currentAccount?.id) }
  );
  // La búsqueda es del servidor desde zenitailab !1053. Antes se filtraba aquí,
  // sobre las 50 filas que había cargadas: si la corrida estaba en la página 2,
  // la pantalla decía "No executions found" — no "no está en esta página", sino
  // que no existe. Y nunca miraba las etiquetas.
  const visibleExecutions = executions;

  useEffect(() => {
    const found = executions
      .map((e) => e.trigger_type)
      .filter(Boolean)
      .map((t) => t.toLowerCase());
    setExtraTriggers((prev) => {
      const nuevos = [...new Set(found)].filter(
        (t) => !TRIGGER_CONFIG[t] && !prev.includes(t)
      );
      // Devolver `prev` cuando no hay nada nuevo evita un render por lectura.
      return nuevos.length ? [...prev, ...nuevos].sort() : prev;
    });
  }, [executions]);

  const triggerOptions = useMemo(
    () => [
      ...Object.entries(TRIGGER_CONFIG).map(([key, cfg]) => [key, cfg.label]),
      ...extraTriggers.map((key) => [key, humanizeTrigger(key)]),
    ],
    [extraTriggers]
  );

  const statusTabs = [
    { id: "ALL",       label: "All" },
    { id: "RUNNING",   label: "Running" },
    { id: "FAILED",    label: "Failed" },
    { id: "WAITING",   label: "Waiting" },
    { id: "COMPLETED", label: "Completed" },
  ];

  const totalPages = Math.ceil(totalCount / LIMIT);

  const closeDropdowns = () => {
    setPeriodOpen(false);
    setTriggerOpen(false);
  };

  return (
    <div onClick={closeDropdowns}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">Runs</h1>
        <p className="text-sm text-gray-500 mt-2">
          {/* Dentro de un agente la tabla está acotada a él: decir "workspace"
              ahí es mentir sobre lo que se está mirando. */}
          {agentId
            ? "Every workflow execution triggered by this agent. Click a row to open its inspector."
            : "Every workflow execution in the workspace. Click a row to open its inspector."}
        </p>
      </div>

      {/* Status tabs.
          Cinco pestañas piden ~430px: envueltas se partían en dos filas, con
          "Completed" sola en la segunda. Una fila que se desplaza cuesta la
          mitad de alto y se lee como lo que es, una sola tira de filtros. */}
      <div className="mb-3 flex gap-1 overflow-x-auto [scrollbar-width:none] md:flex-wrap [&::-webkit-scrollbar]:hidden">
        {statusTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setStatusFilter(tab.id); setPage(0); }}
            className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition ${
              statusFilter === tab.id
                ? "bg-zinc-800 text-white"
                : "text-zinc-400 hover:text-white hover:bg-zinc-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {/* "by name" se quedó corto: el servidor busca también en las
            etiquetas, que es justamente el motivo de que exista. */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-zinc-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or tag…"
            aria-label="Search runs by workflow name or tag"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md pl-8 pr-8 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1.5 rounded p-0.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {/* El aviso ocupa sitio siempre que se esté escribiendo algo corto,
              para que la fila de filtros no dé un salto al aparecer. */}
          {search.trim().length > 0 && search.trim().length < MIN_SEARCH && (
            <p className="absolute left-1 top-full mt-1 text-[11px] text-zinc-500">
              Escribe {MIN_SEARCH} caracteres o más
            </p>
          )}
        </div>

        {/* Trigger filter */}
        <div className="relative" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => { setTriggerOpen(o => !o); setPeriodOpen(false); }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border transition ${
              triggerFilter !== "ALL"
                ? "bg-violet-500/15 border-violet-500/40 text-violet-200"
                : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
            }`}
          >
            <Zap className="w-3.5 h-3.5" />
            Trigger: {triggerFilter === "ALL"
              ? "Any"
              : TRIGGER_CONFIG[triggerFilter.toLowerCase()]?.label ||
                humanizeTrigger(triggerFilter.toLowerCase())}
            <ChevronDown className="w-3 h-3" />
          </button>
          {triggerOpen && (
            <div className="absolute top-full left-0 mt-1 w-44 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl shadow-black/40 z-20 p-1">
              <button
                onClick={() => { setTriggerFilter("ALL"); setTriggerOpen(false); setPage(0); }}
                className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-zinc-800 ${triggerFilter === "ALL" ? "text-white" : "text-zinc-400"}`}
              >
                Any trigger
              </button>
              <div className="border-t border-zinc-800 my-1" />
              {triggerOptions.map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => { setTriggerFilter(key.toUpperCase()); setTriggerOpen(false); setPage(0); }}
                  className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-zinc-800 ${
                    triggerFilter === key.toUpperCase() ? "bg-zinc-800 text-white" : "text-zinc-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Period filter */}
        <div className="relative" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => { setPeriodOpen(o => !o); setTriggerOpen(false); }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border transition ${
              period !== "7d"
                ? "bg-violet-500/15 border-violet-500/40 text-violet-200"
                : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            {PERIODS.find(p => p.id === period)?.label}
            <ChevronDown className="w-3 h-3" />
          </button>
          {periodOpen && (
            <div className="absolute top-full right-0 mt-1 w-44 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl shadow-black/40 z-20 p-1">
              {PERIODS.map(p => (
                <button
                  key={p.id}
                  onClick={() => { setPeriod(p.id); setPeriodOpen(false); setPage(0); }}
                  className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-zinc-800 ${
                    period === p.id ? "bg-zinc-800 text-white" : "text-zinc-300"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => reload()}
          disabled={loading || refreshing}
          aria-label="Refresh"
          title="Refresh runs"
          className="flex items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 p-2 text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-200 disabled:cursor-default disabled:text-zinc-600"
        >
          <RotateCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Table */}
      <div
        className={`rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden transition-opacity ${
          refreshing ? "opacity-60" : ""
        }`}
        aria-busy={refreshing || undefined}
      >
        <div className="runs-grid hidden lg:grid px-4 py-2 border-b border-zinc-800 bg-zinc-900/40 text-[10px] uppercase tracking-wide text-zinc-500">
          <div>Status</div>
          <div>Workflow</div>
          <div>Ver</div>
          <div>Trigger</div>
          {/* "Execution ID" no cabe en 72px y partia la cabecera en dos
              renglones. Lo mismo que "Ver" por "Version" al lado. */}
          <div className="hidden xl:block">ID</div>
          <div>Started</div>
          <div>Duration</div>
          <div>Cost</div>
          <div>Tags</div>
          <div />
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center justify-center py-16 text-sm text-rose-400">
            {error}
          </div>
        )}

        {!loading && !error && visibleExecutions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-2 px-4 text-center">
            <Play className="w-10 h-10 text-zinc-700" />
            {/* Ahora "no hay" es verdad: la búsqueda la resuelve el servidor
                sobre todo el conjunto, no sobre la página cargada. Decir de
                qué término no hay resultados evita releer la caja para
                acordarse de qué se buscó. */}
            <p className="text-sm text-zinc-500">
              {appliedSearch
                ? `No runs match "${appliedSearch}"`
                : "No executions found"}
            </p>
            {appliedSearch && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-xs text-violet-300 hover:text-violet-200"
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {!loading && !error && visibleExecutions.map(execution => {
          const execId = execution.execution_id || execution.id;
          const completedAt = execution.completed_at || execution.ended_at || execution.finished_at;
          const rowProps = {
            onClick: onSelectRun ? () => onSelectRun(execId) : undefined,
          };
          return (
            <Fragment key={execId}>
            {/* La tarjeta llega hasta `lg`, no hasta `md`. La tabla no cabía
                a 768px: con el rail y el padding de la página el contenedor
                son 592px, las columnas pedían 666, y las dos flexibles
                colapsaban a 0px con la fila desbordándose — el nombre del
                workflow no llegaba a pintarse. Medido, no calculado.

                Los mismos datos que la tabla, ninguno menos: estado y nombre
                arriba; versión, disparador, duración y coste en medio; el id
                con la hora abajo; y las etiquetas en su propio renglón, que
                es el único sitio donde pueden crecer. */}
            <div
              {...rowProps}
              className={`border-b border-zinc-900 px-4 py-3 last:border-b-0 lg:hidden ${
                onSelectRun ? "cursor-pointer active:bg-zinc-900/40" : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <StatusBadge status={execution.status} />
                <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">
                  {execution.workflow_name || execution.workflow_id || "—"}
                </span>
                {onSelectRun && <span className="shrink-0 text-sm text-zinc-500">→</span>}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <VersionBadge isTest={execution.is_test} version={execution.version_number} />
                <TriggerBadge type={execution.trigger_type} />
                <span className="font-mono text-xs text-zinc-300">
                  {formatDuration(execution.started_at, completedAt)}
                </span>
                {/* El coste se cuela junto a la duración porque es el otro
                    número con el que se compara una corrida, y aquí no hay
                    encabezado que diga cuál es cuál: de ahí la unidad. El
                    punto separa dos números monoespaciados que pegados se
                    leían como uno solo. */}
                <span className="text-xs text-zinc-600" aria-hidden="true">·</span>
                <span className="font-mono text-xs text-zinc-300">
                  {formatCoins(execution.cost)}
                  <span className="ml-1 font-sans text-[10px] text-zinc-500">coins</span>
                </span>
              </div>
              {execution.tags?.length > 0 && (
                <div className="mt-1.5">
                  <TagList tags={execution.tags} max={3} onSelectTag={setSearch} />
                </div>
              )}
              <div className="mt-1.5 flex items-center gap-2 text-xs text-zinc-500">
                <span className="truncate font-mono">{shortenId(execId)}</span>
                <span aria-hidden="true">·</span>
                <span className="shrink-0">{formatStarted(execution.started_at)}</span>
              </div>
            </div>

            <div
              {...rowProps}
              className={`runs-grid hidden lg:grid px-4 py-2.5 border-b border-zinc-900 last:border-b-0 transition ${
                onSelectRun ? "hover:bg-zinc-900/40 cursor-pointer" : ""
              }`}
            >
              <div><StatusBadge status={execution.status} /></div>
              <div className="text-sm text-zinc-200 truncate" title={execution.workflow_name || undefined}>
                {execution.workflow_name || execution.workflow_id || "—"}
              </div>
              <div><VersionBadge isTest={execution.is_test} version={execution.version_number} /></div>
              <div><TriggerBadge type={execution.trigger_type} /></div>
              <div className="hidden xl:block text-xs font-mono text-zinc-500 truncate">{shortenId(execId)}</div>
              <div className="text-xs text-zinc-400">{formatStarted(execution.started_at)}</div>
              <div className="text-xs text-zinc-300 font-mono">
                {formatDuration(execution.started_at, completedAt)}
              </div>
              {/* Monoespaciado como la duración, que es su vecina: son los dos
                  números con los que se compara una corrida con otra. */}
              <div className="text-xs text-zinc-300 font-mono">{formatCoins(execution.cost)}</div>
              <div className="min-w-0">
                <TagList tags={execution.tags} onSelectTag={setSearch} />
              </div>
              <div className="text-zinc-500 hover:text-white text-sm">→</div>
            </div>
            </Fragment>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
          <div>
            Showing {Math.min((page + 1) * LIMIT, totalCount)} of {totalCount} runs
          </div>
          <div className="flex gap-1">
            <button
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
              className="px-2 py-1 rounded hover:bg-zinc-900 text-zinc-600 disabled:opacity-40"
            >
              ‹
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i).map(i => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`px-2 py-1 rounded ${page === i ? "bg-zinc-800 text-white" : "hover:bg-zinc-900"}`}
              >
                {i + 1}
              </button>
            ))}
            {totalPages > 5 && <span className="px-2 py-1 text-zinc-700">…</span>}
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              className="px-2 py-1 rounded hover:bg-zinc-900 disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
